export interface StatusColorOption {
  value: string
  label: string
  previewHex: string
  description?: string
}

/**
 * Danh sách màu ngữ nghĩa (Semantic Status Colors) chuẩn hóa cho toàn hệ thống
 * Tuân thủ tiêu chuẩn Ant Design / Bootstrap, tránh hardcode mã màu cứng.
 */
export const SEMANTIC_STATUS_COLORS: readonly StatusColorOption[] = [
  {
    value: 'default',
    label: 'Mặc định / Bản nháp (default)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#8c8c8c',
    description: 'Bản nháp, chưa kích hoạt, tạm dừng, hủy',
  },
  {
    value: 'processing',
    label: 'Đang xử lý (processing)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#1677ff',
    description: 'Đang ký, đang xử lý, nội bộ',
  },
  {
    value: 'warning',
    label: 'Cảnh báo / Chờ duyệt (warning)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#faad14',
    description: 'Chờ kích hoạt, chờ phát hành, chờ duyệt',
  },
  {
    value: 'success',
    label: 'Thành công (success)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#52c41a',
    description: 'Đang hoạt động, hoàn tất, đã ký, đã phát hành, thành công',
  },
  {
    value: 'error',
    label: 'Lỗi / Khóa / Thu hồi (error)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#ff4d4f',
    description: 'Đã khóa, đã thu hồi, thất bại, từ chối',
  },
  {
    value: 'volcano',
    label: 'Đã hủy / Cảnh báo đậm (volcano)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#fa541c',
    description: 'Đã hủy, cảnh báo đặc biệt',
  },
  {
    value: 'purple',
    label: 'Tím - Phân loại đặc thù (purple)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#722ed1',
    description: 'Đại diện tổ chức, nhóm đặc thù',
  },
  {
    value: 'cyan',
    label: 'Xanh lơ - Phân loại mở rộng (cyan)',
    // ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
    previewHex: '#13c2c2',
    description: 'Khách hàng, nhóm đối tượng phụ',
  },
] as const

export const SEMANTIC_STATUS_SET = new Set<string>([
  'default',
  'processing',
  'warning',
  'success',
  'error',
  'volcano',
  'purple',
  'cyan',
])

/**
 * Bảng quy đổi tự động từ tên màu vật lý cũ (green, red, gold...) sang Semantic Status Key
 */
export const LEGACY_COLOR_TO_SEMANTIC: Record<string, string> = {
  green: 'success',
  red: 'error',
  gold: 'warning',
  orange: 'warning',
  yellow: 'warning',
  blue: 'processing',
  gray: 'default',
  grey: 'default',
}

/**
 * Chuẩn hóa bất kỳ mã màu nào (cũ hoặc mới) về Semantic Key chuẩn
 */
export const normalizeColorToSemantic = (color?: string | null): string => {
  if (!color || typeof color !== 'string') return 'default'
  const trimmed = color.trim().toLowerCase()
  return LEGACY_COLOR_TO_SEMANTIC[trimmed] ?? trimmed
}

export const BADGE_STATUS_SET = new Set<string>([
  'default',
  'processing',
  'warning',
  'success',
  'error',
])

/**
 * Phân giải prop cho UiBadge từ mã màu semantic hoặc màu preset.
 * Ant Design Badge: nếu là 1 trong 5 trạng thái chuẩn thì nhận `status`,
 * nếu là màu khác (purple, cyan, hex) thì nhận `color`.
 */
export const resolveBadgeProps = (
  color?: string | null
): { status?: 'success' | 'processing' | 'default' | 'error' | 'warning'; color?: string } => {
  if (!color || typeof color !== 'string') {
    return { status: 'default' }
  }
  const cleanColor = normalizeColorToSemantic(color)
  if (BADGE_STATUS_SET.has(cleanColor)) {
    return { status: cleanColor as 'success' | 'processing' | 'default' | 'error' | 'warning' }
  }
  return { color: cleanColor }
}

/** Màu riêng biệt cho tab Tất cả (Xám than đậm / Slate) đại diện cho tổng số bản ghi */
// ds-guard-ignore: bảng màu trạng thái có sẵn từ trước B8, chuyển nguyên trạng; gom về token khi làm lại màu trạng thái
export const TAB_ALL_BADGE_COLOR = '#475569'

/**
 * Lấy mã màu Hex/CSS chuẩn cho Badge Count (chữ số đếm hình viên thuốc tròn) từ Semantic Key
 * Luôn giữ nguyên màu của trạng thái, không gán xám khi count = 0
 */
export const getBadgeCountColor = (colorKey?: string | null): string => {
  if (!colorKey || colorKey === 'all') return TAB_ALL_BADGE_COLOR
  const normalized = normalizeColorToSemantic(colorKey)
  const found = SEMANTIC_STATUS_COLORS.find((opt) => opt.value === normalized)
  return found?.previewHex ?? normalized
}

/**
 * Quy đổi mã màu SystemKey sang tone chuẩn của UiButtonCircle ('neutral' | 'primary' | 'success' | 'warning' | 'danger')
 */
export const resolveButtonCircleTone = (
  color?: string | null
): 'neutral' | 'primary' | 'success' | 'warning' | 'danger' => {
  if (!color) return 'neutral'
  const c = normalizeColorToSemantic(color)
  if (c === 'error' || c === 'volcano') return 'danger'
  if (c === 'success') return 'success'
  if (c === 'warning') return 'warning'
  if (c === 'processing' || c === 'primary') return 'primary'
  return 'neutral'
}

/**
 * Quy đổi mã màu SystemKey sang mã biến CSS dùng cho icon SVG
 */
export const resolveIconColorCss = (color?: string | null): string => {
  if (!color) return 'inherit'
  const c = normalizeColorToSemantic(color)
  if (c === 'error' || c === 'volcano') return 'var(--ant-color-error)'
  if (c === 'success') return 'var(--ant-color-success)'
  if (c === 'warning') return 'var(--ant-color-warning)'
  if (c === 'processing' || c === 'primary') return 'var(--ant-color-primary)'
  if (c.startsWith('#') || c.startsWith('rgb')) return c
  return 'inherit'
}
